import { useEffect, useRef } from "react";
import Cookies from "js-cookie";
import { FILTERS_COOKIE_KEY, DEFAULT_MARKER_COLOR } from "../utils/constants";
import type { Filter, FilterType } from "../context/VictimsAndKillersContext";

export type PersistFilter = {
  id: number;
  killerId: string | null;
  color: string;
  visible: boolean;
  type: FilterType;
  from: string | null;
  to: string | null;
  country: string | null;
  region: string | null;
};

function normalizeCountry(value: unknown): string {
  if (typeof value !== "string") return "United States";
  return value.trim().toLowerCase() === "usa" ? "United States" : value;
}

function sanitizeFilters(raw: unknown): PersistFilter[] | null {
  if (!Array.isArray(raw)) return null;
  const allowed = new Set<FilterType>(["serialkiller", "unsolved", "missing"]);
  const out: PersistFilter[] = [];
  for (const r of raw as any[]) {
    if (!r || typeof r !== "object") continue;
    const id = Number(r.id);
    const type = r.type as FilterType;
    if (!Number.isFinite(id) || !allowed.has(type)) continue;
    out.push({
      id,
      killerId: typeof r.killerId === "string" ? r.killerId : null,
      color: typeof r.color === "string" ? r.color : DEFAULT_MARKER_COLOR,
      visible: typeof r.visible === "boolean" ? r.visible : true,
      type,
      from: typeof r.from === "string" ? r.from : null,
      to: typeof r.to === "string" ? r.to : null,
      country: normalizeCountry(r.country),
      region:
        typeof r.region === "string" || r.region === null ? r.region : null,
    });
  }
  return out.length ? out : null;
}

function loadFiltersFromCookie(): PersistFilter[] | null {
  const str = Cookies.get(FILTERS_COOKIE_KEY);
  if (!str) return null;
  try {
    return sanitizeFilters(JSON.parse(str));
  } catch {
    return null;
  }
}

function mapToPersist(filters: Filter[]): PersistFilter[] {
  return filters.map((r) => ({
    id: r.id,
    killerId: r.killerId ?? null,
    color: r.color,
    visible: r.visible !== false,
    type: r.type,
    from: r.from ?? null,
    to: r.to ?? null,
    country: r.country ?? "United States",
    region: r.region ?? null,
  }));
}

export function useInitialFiltersFromCookie(): PersistFilter[] | null {
  const ref = useRef<PersistFilter[] | null>(null);
  if (ref.current === null) {
    ref.current = loadFiltersFromCookie();
  }
  return ref.current;
}

export function usePersistFiltersCookie(filters: Filter[]) {
  useEffect(() => {
    try {
      Cookies.set(
        FILTERS_COOKIE_KEY,
        JSON.stringify(
          mapToPersist(filters).map((filter) => ({
            ...filter,
            country: normalizeCountry(filter.country),
          })),
        ),
        {
        expires: 365,
        sameSite: "Lax",
        path: "/",
      },
      );
    } catch {
      // ignore cookie write errors
    }
  }, [filters]);
}